import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../ui/select";
import Button from "../../button-v1";
import styles from "./styles.module.css";
import { Checkbox } from "../../ui/checkbox";
import {
  createPatientContact,
  getPatientContacts,
  updatePatientContact,
} from "../../../services/patient/settings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FaPhone, FaMessage } from "react-icons/fa6";
import { toast } from "react-hot-toast";
import Spinner from "../../spinner";
import Input from "../../ui/input";

const contactSchema = z.object({
  label: z
    .string()
    .min(1, "Contact label is required")
    .max(50, "Contact label must be less than 50 characters"),
  number: z
    .string()
    .min(1, "Please enter a valid phone number")
    .regex(/^[0-9+\-\s()]*$/, "Please enter a valid phone number"),
  is_primary: z.boolean().default(false),
  // relation: z.enum(["landline", "work", "personal", "mobile", "relative"], {
  //   message: "Please select a contact relation",
  // }),
  number_type: z.enum(["messaging", "calling"], {
    message: "Please select a contact type",
  }),
});

const contactTypes = [
  { label: "Message", value: "messaging", icon: <FaMessage /> },
  { label: "Call", value: "calling", icon: <FaPhone /> },
];

const defaultSchema = {
  relation: "",
  label: "",
  number: "",
  number_type: "",
  is_primary: false,
};

const AddContacts = ({
  open,
  setOpen,
  contactId,
  setAdditionalNumbers,
  contactInfo,
}) => {
  const queryClient = useQueryClient();
  const patientId = sessionStorage.getItem("_pt_id");
  const locationId = sessionStorage.getItem("_loc_id");
  const accountId = sessionStorage.getItem("acc_id");

  const {
    data: patientContacts,
    isLoading: isLoadingPatientContacts,
    error: contactsError,
  } = useQuery(
    ["patientContacts", patientId],
    () => getPatientContacts(patientId, locationId),
    {
      enabled: !!patientId && !!locationId && !!accountId,
      retry: false,
      onError: (error) => {
        console.error("Error fetching contacts:", error);
        toast.error("Failed to load contacts");
      },
    }
  );

  const {
    mutate: updateContactMutation,
    isLoading: isLoadingUpdateContact,
    isSuccess: isSuccessUpdateContact,
    isError: isErrorUpdateContact,
    error: updateContactError,
  } = useMutation((data) => updatePatientContact(data, locationId, contactId), {
    onSuccess: () => {
      toast.success("Contact updated successfully");
      queryClient.invalidateQueries(["patientContacts"]);
      setOpen(false);
    },
    onError: (error) => {
      Object.keys(defaultSchema).forEach((key) => {
        if (error.response.data[key]) {
          setError(key, {
            type: "manual",
            message: error.response.data[key],
          });
        }
      });
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    setError,
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: defaultSchema,
  });

  const {
    mutate: createContactMutation,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useMutation((data) => createPatientContact(data, patientId, locationId), {
    onSuccess: () => {
      toast.success("Contact added successfully");
      queryClient.invalidateQueries(["patientContacts"]);
      setOpen(false);
    },
    onError: (error) => {
      Object.keys(defaultSchema).forEach((key) => {
        if (error.response.data[key]) {
          setError(key, {
            type: "manual",
            message: error.response.data[key],
          });
        }
      });
    },
  });

  const number_type = watch("number_type");
  const relation = watch("relation");
  const is_primary = watch("is_primary");

  const onSubmit = (data) => {
    if (contactId) {
      updateContactMutation(data);
    } else {
      createContactMutation(data);
    }
    setOpen(false);
  };

  useEffect(() => {
    if (patientContacts && contactId) {
      const contact = patientContacts.find(
        (contact) => contact.id === contactId
      );

      if (contact) {
        setValue("label", contact.label);
        setValue("number", contact.number);
        setValue("number_type", contact.number_type);
        setValue("is_primary", contact.is_primary);
        setValue("relation", contact.relation);
      } else {
        reset();
      }
    }
  }, [patientContacts, contactId]);

  return (
    <Dialog open={open} setOpen={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Contact Number</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoadingPatientContacts ? (
            <section className="content-center">
              <Spinner />
            </section>
          ) : (
            <form className={styles.input_fields}>
              {/* Contact Label */}
              <div className={styles.input_wrapper}>
                <label className="label">Contact Label</label>
                <input
                  type="text"
                  placeholder="Enter contact label"
                  className="input_field"
                  {...register("label")}
                />
                {errors.label && (
                  <span className="error-message">{errors.label.message}</span>
                )}
              </div>

              {/* Contact Number */}
              <div className={styles.input_wrapper}>
                <label className="label">Contact Number</label>
                <Controller
                  name="number"
                  control={control}
                  render={({ field }) => (
                    <Input
                      mask={true}
                      maskPattern="(999) 999-9999"
                      type="tel"
                      placeholder="Enter contact number"
                      name={field.name}
                      value={field.value || ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
                {errors.number && (
                  <span className="error-message">{errors.number.message}</span>
                )}
              </div>

              {/* Contact Type */}

              <section className={styles.input_container}>
                <div className={styles.input_wrapper}>
                  <label className="label">Contact Type</label>
                  <Select
                    value={number_type}
                    onChange={(value) => setValue("number_type", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a contact type">
                        <p>
                          {contactTypes.find(
                            (type) => type.value === number_type
                          )?.label || "Select a contact type"}
                        </p>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {contactTypes.map((type) => (
                        <SelectItem
                          key={type.value}
                          value={type.value}
                          className={styles.select_item}
                        >
                          <span>{type.icon}</span>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.number_type && (
                    <span className="error-message">
                      {errors.number_type.message}
                    </span>
                  )}
                </div>
                {/* <div className={styles.input_wrapper}>
                  <label className="label">Primary Number</label>
                  <Select
                    value={is_primary}
                    onChange={(value) => setValue("is_primary", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a contact type">
                        <p>{is_primary ? "Yes" : "No"}</p>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={true}>Yes</SelectItem>
                      <SelectItem value={false}>No</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.is_primary && (
                    <span className="error-message">
                      {errors.is_primary.message}
                    </span>
                  )}
                </div> */}
              </section>

              <div className={styles.input_wrapper}>
                <section className={styles.checkbox_wrapper}>
                  <section>
                    <Checkbox
                      size="large"
                      checked={relation === "landline"}
                      onCheckedChange={(checked) => {
                        setValue("relation", "landline");
                      }}
                    />
                    <label>Landline</label>
                  </section>
                  <section>
                    <Checkbox
                      size="large"
                      checked={relation === "work"}
                      onCheckedChange={(checked) => {
                        setValue("relation", "work");
                      }}
                    />
                    <label>Work</label>
                  </section>
                  <section>
                    <Checkbox
                      size="large"
                      checked={relation === "personal"}
                      onCheckedChange={(checked) => {
                        setValue("relation", "personal");
                      }}
                    />
                    <label>Personal</label>
                  </section>
                  <section>
                    <Checkbox
                      size="large"
                      checked={relation === "mobile"}
                      onCheckedChange={(checked) => {
                        setValue("relation", "mobile");
                      }}
                    />
                    <label>Mobile</label>
                  </section>
                  <section>
                    <Checkbox
                      size="large"
                      checked={relation === "relative"}
                      onCheckedChange={(checked) => {
                        setValue("relation", "relative");
                      }}
                    />
                    <label>Relative</label>
                  </section>
                </section>
                {errors.relation && (
                  <span className="error-message">
                    {errors.relation.message}
                  </span>
                )}
              </div>
            </form>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="normal" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          {contactId ? (
            <Button
              variant="primary"
              type="submit"
              loading={isLoadingUpdateContact}
              onClick={handleSubmit(onSubmit)}
            >
              Update Contact
            </Button>
          ) : (
            <Button
              variant="primary"
              type="submit"
              loading={isLoading}
              onClick={handleSubmit(onSubmit)}
            >
              Add Contact
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddContacts;
